import { getContext, setContext } from 'svelte';
import { type DeviceName, deviceNames } from './device';
import { objectEntries, objectFromEntries } from './object-utils';
import { screenSizes, type MaybeResponsive, type Responsive } from './responsive-utils';
import { createSafeCaster } from './safeCaster';

type CurrentWidth = Record<DeviceName, { relativeWidth: number; pixelWidth: number }>;

const PageSectionBackgroundOptions = [
  'light',
  'medium',
  'dark',
  'brand',
  'magenta',
  'red',
  'green',
  'blue',
  'none',
] as const satisfies Array<string>;
export type PageSectionBackground = (typeof PageSectionBackgroundOptions)[number];
export const pageSectionBgCaster = createSafeCaster(PageSectionBackgroundOptions);

export const lightColors: Array<PageSectionBackground> = ['light', 'medium', 'brand'];

export type ContainerContext = {
  parent?: ContainerContext;
  /**
   * Number between 0 and 1 representing the width in relation to its parents width. If
   * the current width is 50% of its parent, this value will be 0.5.
   * sm, md and lg are the breakpoints for the container.
   */
  width: Record<DeviceName, number>;
  maxPixelWidth?: Record<DeviceName, number>;
  getCurrentWidth: () => CurrentWidth;
  backgroundColor?: Responsive<PageSectionBackground>;
};

export const containerContextKey = Symbol('athena-container-context');

type ColorContext = { value: Responsive<PageSectionBackground> };
const defaultBackgroundColor: Responsive<PageSectionBackground> = {
  lg: 'light',
  md: 'light',
  sm: 'light',
};

export const rune = <T>(initialValue: T) => {
  let _rune = $state(initialValue);

  return {
    get value() {
      return _rune;
    },
    set value(v: T) {
      _rune = v;
    },
  };
};

const colorContextKey = Symbol('athena-color-context');
export const getColorContext = () => {
  const colorContext = getContext<ColorContext | undefined>(colorContextKey);
  if (!colorContext) {
    console.error('ColorContext has not been initialized');
    return rune(defaultBackgroundColor);
  }
  return colorContext;
};

export const getColorContextValue = (
  backgroundColor: MaybeResponsive<PageSectionBackground>,
): Responsive<PageSectionBackground> =>
  typeof backgroundColor === 'string'
    ? { lg: backgroundColor, sm: backgroundColor, md: backgroundColor }
    : backgroundColor;

export const initializeColorContext = (newValue: typeof defaultBackgroundColor): ColorContext => {
  const store = rune(newValue);
  setContext(colorContextKey, store);
  return store;
};

export const responsiveBackgrounds: Responsive<Record<PageSectionBackground, string>> = {
  lg: {
    light: 'lg:bg-white',
    medium: 'lg:bg-st-gray-100',
    dark: 'lg:bg-st-black lg:text-st-white',
    brand: 'lg:bg-st-yellow lg:text-st-black',
    magenta: 'lg:bg-st-pink lg:text-st-white',
    red: 'lg:bg-st-red lg:text-st-white',
    green: 'lg:bg-st-green lg:text-st-white',
    blue: 'lg:bg-st-blue lg:text-st-white',
    none: 'lg:bg-transparent',
  },
  md: {
    light: 'md:bg-white',
    medium: 'md:bg-st-gray-100',
    dark: 'md:bg-st-black md:text-st-white',
    brand: 'md:bg-st-yellow md:text-st-black',
    magenta: 'md:bg-st-pink md:text-st-white',
    red: 'md:bg-st-red md:text-st-white',
    green: 'lg:bg-st-green lg:text-st-white',
    blue: 'lg:bg-st-blue lg:text-st-white',
    none: 'md:bg-transparent',
  },
  sm: {
    light: 'bg-white',
    medium: 'bg-st-gray-100',
    dark: 'bg-st-black text-st-white',
    brand: 'bg-st-yellow text-st-black',
    magenta: 'bg-st-pink text-st-white',
    red: 'bg-st-red text-st-white',
    green: 'lg:bg-st-green lg:text-st-white',
    blue: 'lg:bg-st-blue lg:text-st-white',
    none: 'bg-transparent',
  },
} as const;

export const backgrounds = {
  light: ['bg-white'],
  medium: ['bg-st-gray-100'],
  dark: ['bg-st-black', 'text-st-white'],
  brand: ['bg-st-yellow', 'text-st-black'],
  magenta: ['bg-st-pink', 'text-st-white'],
  red: ['bg-st-red', 'text-st-white'],
  green: ['bg-st-green', 'text-st-white'],
  blue: ['bg-st-blue', 'text-st-white'],
  none: ['bg-transparent'],
} as const;

export const getContainerContext = () =>
  getContext<ContainerContext | undefined>(containerContextKey);

export const setContainerContextMaxPixelWidth = (maxPixelWidth: Record<DeviceName, number>) => {
  const parent = getContainerContext();

  const width = { sm: 1, md: 1, lg: 1 };
  const newContext: ContainerContext = {
    parent,
    maxPixelWidth,
    width,
    getCurrentWidth: () => getCurrentWidth(width, newContext),
  };
  setContext(containerContextKey, newContext);
};

export const setContainerContextWidth = (width: Record<DeviceName, number>) => {
  const parent = getContainerContext();
  setContext<Partial<ContainerContext>>(containerContextKey, {
    width,
    parent,
    getCurrentWidth: () => getCurrentWidth(width, parent),
  });
};

const getCurrentWidth = (
  width: Record<DeviceName, number>,
  parent: ContainerContext | undefined,
) => {
  const totalWidths: Record<DeviceName, number> = { ...width };
  let maxPixelWidth: Record<DeviceName, number> | undefined = undefined;
  let currentParent = parent;
  while (currentParent) {
    for (const device of deviceNames) {
      totalWidths[device] *= currentParent.width[device];
    }
    if (currentParent.maxPixelWidth) {
      maxPixelWidth = currentParent.maxPixelWidth;
      break;
    }
    currentParent = currentParent.parent;
  }

  if (!maxPixelWidth) {
    // This should only happen on pages without a <PageSection>, which should be rare and mostly an error in itself.
    // If there's no PageSection it most likely means that the content is displayed at fullscreen width.
    maxPixelWidth = screenSizes;
  }

  const pageSizes: Record<DeviceName, number> = objectFromEntries(
    deviceNames.map((deviceName) => [
      deviceName as DeviceName,
      Math.min(screenSizes[deviceName], maxPixelWidth![deviceName]),
    ]),
  );

  return objectFromEntries(
    objectEntries(totalWidths).map(([deviceName, width]) => [
      deviceName,
      { relativeWidth: width, pixelWidth: width * pageSizes[deviceName] },
    ]),
  );
};
